import { useId, useState } from "react";
import {
  ArrowUpRight,
  BookOpen,
  FileText,
  Workflow,
  Database,
  MessageSquareText,
  FileCheck2,
  GitBranch,
  Code2,
} from "lucide-react";
import {
  ActionLink,
  Eyebrow,
  Reveal,
  ScrollHeading,
  useSceneActivity,
} from "./ui";

const examples = [
  {
    title: "Knowledge assistants",
    icon: BookOpen,
    description:
      "Connect an assistant to your approved documents and systems so people can find relevant answers with source references.",
    output: "Answers with context",
    detail: "Search approved knowledge",
    outputIcon: MessageSquareText,
    feeds: [0, 1],
  },
  {
    title: "Document workflows",
    icon: FileText,
    description:
      "Extract key details, summarize long documents, and route information into your applications, with review steps where needed.",
    output: "Documents into data",
    detail: "Extract · Summarize · Review",
    outputIcon: FileCheck2,
    feeds: [0],
  },
  {
    title: "Connected automation",
    icon: Workflow,
    description:
      "Connect AI to your tools and APIs to draft updates, handle routine steps, and prepare actions for approval.",
    output: "Actions across your tools",
    detail: "Prepare · Approve · Run",
    outputIcon: GitBranch,
    feeds: [1, 2],
  },
];
function project(latitude, longitude) {
  const radius = 134;
  const x = radius * Math.cos(latitude) * Math.cos(longitude);
  const y = radius * Math.sin(latitude);
  const z = radius * Math.cos(latitude) * Math.sin(longitude);
  const tiltedY = y * Math.cos(0.55) - z * Math.sin(0.55);
  const depth = y * Math.sin(0.55) + z * Math.cos(0.55);
  return {
    x: 354 + x * Math.cos(-0.2) - tiltedY * Math.sin(-0.2),
    y: 232 + x * Math.sin(-0.2) + tiltedY * Math.cos(-0.2),
    depth,
  };
}
function curve(points) {
  return points
    .map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(" ");
}
const latitudeLines = Array.from({ length: 13 }, (_, i) =>
  curve(
    Array.from({ length: 100 }, (_, j) =>
      project(((i - 6) * Math.PI) / 15, (j / 99) * Math.PI * 2),
    ),
  ),
);
const longitudeLines = Array.from({ length: 18 }, (_, i) =>
  curve(
    Array.from({ length: 80 }, (_, j) =>
      project(-Math.PI / 2 + (j / 79) * Math.PI, (i / 18) * Math.PI * 2),
    ),
  ),
);
const nodes = Array.from({ length: 30 }, (_, i) =>
  project(((((i * 7) % 13) - 6) * Math.PI) / 15, i * 2.39996),
).filter((p) => p.depth > 0);
const feeds = [
  {
    label: "DOCUMENTS",
    y: 112,
    icon: FileText,
    path: "M80 112C146 112 159 157 231 174",
  },
  {
    label: "BUSINESS DATA",
    y: 232,
    icon: Database,
    path: "M80 232C132 232 165 232 220 232",
  },
  {
    label: "YOUR TOOLS",
    y: 352,
    icon: Workflow,
    path: "M80 352C145 352 179 316 241 307",
  },
];
function AIVisual({ selected }) {
  const { ref, active } = useSceneActivity();
  const id = useId().replace(/:/g, "");
  const item = examples[selected],
    OutputIcon = item.outputIcon;
  return (
    <div
      ref={ref}
      className="ai-visual"
      data-running={active}
      role="img"
      aria-label={`AI integration concept: your documents, business data, and tools connect through AI. ${item.output}.`}
    >
      <div aria-hidden="true">
        <div className="ai-visual-top">
          <span>
            <i />
            CONNECTED INTELLIGENCE
          </span>
          <span>INTEGRATION CONCEPT</span>
        </div>
        <div className="ai-visual-sources">Documents · Data · Tools</div>
        <svg className="ai-visual-svg" viewBox="0 0 620 500" fill="none">
          <defs>
            <radialGradient id={`${id}-halo`}>
              <stop stopColor="#fddf47" stopOpacity=".13" />
              <stop offset="1" stopColor="#fddf47" stopOpacity="0" />
            </radialGradient>
            <linearGradient id={`${id}-core`} x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="#35301a" />
              <stop offset="1" stopColor="#0b0b09" />
            </linearGradient>
            <filter
              id={`${id}-glow`}
              x="-100%"
              y="-100%"
              width="300%"
              height="300%"
            >
              <feGaussianBlur stdDeviation="2" />
            </filter>
          </defs>
          <ellipse
            cx="354"
            cy="232"
            rx="227"
            ry="199"
            fill={`url(#${id}-halo)`}
          />
          <ellipse
            cx="354"
            cy="232"
            rx="186"
            ry="80"
            transform="rotate(-28 354 232)"
            stroke="#fddf47"
            strokeOpacity=".14"
          />
          <ellipse
            className="ai-orbit"
            cx="354"
            cy="232"
            rx="164"
            ry="164"
            stroke="#b7ad7a"
            strokeOpacity=".2"
            strokeDasharray="2 10"
          />
          {feeds.map(({ label, y, icon: Icon, path }, i) => (
            <g
              key={label}
              className={`ai-feed ${item.feeds.includes(i) ? "is-selected" : ""}`}
            >
              <path d={path} className="ai-feed-track" />
              <path
                d={path}
                pathLength="1"
                className="ai-signal"
                style={{ animationDelay: `${i * -0.95}s` }}
              />
              <circle cx="58" cy={y} r="22" className="ai-source-circle" />
              <Icon x="48" y={y - 10} size={20} strokeWidth={1.35} />
              <text
                x="58"
                y={y + 43}
                textAnchor="middle"
                className="ai-source-label"
              >
                {label}
              </text>
            </g>
          ))}
          <g className="ai-lattice">
            {latitudeLines.map((d, i) => (
              <path
                key={`lat-${i}`}
                d={d}
                stroke="#fddf47"
                strokeWidth=".7"
                strokeOpacity={0.13 + (i % 3) * 0.065}
              />
            ))}
            {longitudeLines.map((d, i) => (
              <path
                key={`lon-${i}`}
                d={d}
                stroke="#fff2b2"
                strokeWidth=".65"
                strokeOpacity={0.15 + (i % 4) * 0.045}
              />
            ))}
            {[3, 8].map((i) => (
              <path
                key={i}
                d={latitudeLines[i]}
                pathLength="1"
                className="ai-lattice-signal"
                style={{ animationDelay: `${i * -0.4}s` }}
              />
            ))}
            {nodes.map((p, i) => (
              <g
                key={i}
                className="ai-neuron"
                style={{ animationDelay: `${i * -0.35}s` }}
              >
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="5"
                  fill="#fddf47"
                  fillOpacity=".25"
                  filter={`url(#${id}-glow)`}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={i % 4 === 0 ? 2.6 : 1.6}
                  fill="#fff3b8"
                />
              </g>
            ))}
          </g>
          <circle
            cx="354"
            cy="232"
            r="37"
            fill={`url(#${id}-core)`}
            stroke="#fddf47"
            strokeOpacity=".65"
          />
          <circle
            cx="354"
            cy="232"
            r="44"
            stroke="#fddf47"
            strokeOpacity=".18"
          />
          <text x="354" y="241" textAnchor="middle" className="ai-core-label">
            AI
          </text>
          <text x="354" y="52" textAnchor="middle" className="ai-core-caption">
            CONTEXT + REASONING
          </text>
          <path
            d="M468 305C509 332 516 344 516 382"
            stroke="#a1903f"
            strokeWidth="1"
          />
          <path
            className="ai-output-signal"
            d="M468 305C509 332 516 344 516 382"
            pathLength="1"
          />
        </svg>
        <div className="ai-output">
          <span className="ai-output-icon">
            <OutputIcon size={20} strokeWidth={1.4} />
          </span>
          <div>
            <strong>{item.output}</strong>
            <span>{item.detail}</span>
          </div>
          <ArrowUpRight size={17} />
        </div>
      </div>
    </div>
  );
}
export function AIIntegrations() {
  const [selected, setSelected] = useState(0);
  const id = useId().replace(/:/g, "");
  return (
    <section
      id="ai"
      className="ai-section section-pad"
      aria-label="AI integration and automation"
    >
      <div className="wrap">
        <Reveal className="ai-section-heading">
          <div>
            <Eyebrow number="03">AI integration &amp; automation</Eyebrow>
            <div>
              <ScrollHeading
                lines={[
                  { text: "AI that works", muted: false },
                  { text: "where you work.", muted: true },
                ]}
              />
            </div>
          </div>
          <p>
            Bring AI into the applications, knowledge, and workflows your team
            already uses, with integrations built around a specific business
            outcome.
          </p>
        </Reveal>
        <Reveal className="ai-panel">
          <div className="ai-panel-copy">
            <span className="ai-explore-label">EXPLORE THE POSSIBILITIES</span>
            <div
              className="ai-options"
              role="group"
              aria-label="AI integration examples"
            >
              {examples.map((example, i) => {
                const Icon = example.icon;
                return (
                  <button
                    key={example.title}
                    className="ai-option"
                    onClick={() => setSelected(i)}
                    aria-pressed={selected === i}
                    aria-controls={`${id}-details`}
                  >
                    <Icon size={20} strokeWidth={1.4} />
                    <span>{example.title}</span>
                    <ArrowUpRight size={17} />
                  </button>
                );
              })}
            </div>
            <div
              className="ai-description"
              id={`${id}-details`}
              role="region"
              aria-label="AI integration details"
              aria-live="polite"
            >
              <p>{examples[selected].description}</p>
            </div>
            <ActionLink
              className="ai-project-link"
              href="mailto:quantumcodecorp@gmail.com?subject=AI%20integration%20project"
            >
              Discuss an AI project
            </ActionLink>
          </div>
          <AIVisual selected={selected} />
        </Reveal>
        <Reveal className="ai-delivery-note">
          <Code2 size={21} strokeWidth={1.4} />
          <p>
            <strong>Build faster with AI, too.</strong> We use AI-assisted
            development to accelerate prototyping and reduce repetitive work,
            with engineering review and testing throughout.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
